import { schemaAlias } from "@/pages/NewStudio/pipelineView";
import type { SlotIntent } from "@/pages/slotIntent";
import type { Encoding, SchemaConfig, StageConfig } from "@/types/api";
import { sourceEncodingRule } from "@/utils/providerCapabilities";
import {
  parseResourceId,
  parseSchemaRowId,
  type StagedResource,
  type StagedSchema,
} from "./pipelineStaging";
import {
  PIPELINE_CODE_TEMPLATE,
  PLACEHOLDER_STAGES,
  TEMPLATE_NAME,
} from "./pipelineTemplate";

// The "Full pipeline code" view: /studio's pipeline configuration JSON
// (usePipelineState's PipelineDoc shape) assembled from the staged sections.
// Schema attachments become a top-level schemas block (aliased by the
// schema's real name, referencing the registry entry by its immutable id)
// plus per-stage schema_id refs pointing at the aliases — the shape the
// engine's SchemaBuilder resolves at provision time.

function defaultEncoding(provider: string | undefined): Encoding {
  return provider === "mongodb" ? "bson" : "json";
}

// A Kafka or Pub/Sub source has no engine default for its input encoding
// and fails to start without one (TTTL-268); json is the Stages default.
// Providers that decide or default the encoding themselves get no key, so
// a document is regenerated as it was.
function defaultInputEncoding(
  provider: string | undefined,
): Encoding | undefined {
  return sourceEncodingRule(provider).kind === "required" ? "json" : undefined;
}

/** A user edit of the code view, pinned to the generated code it started
 *  from — a staging change regenerates the code and invalidates the draft. */
export interface PipelineCodeDraft {
  text: string;
  basedOn: string;
}

/** The document the code view shows: empty while a pipeline is on its way
 *  in (`opening`) — with nothing staged the generated code is the template,
 *  and painting that (with its "your-…" slot hints) for the length of the
 *  open fetches reads as a flash of the wrong pipeline — else the draft
 *  resolved over the generated code (resolveCodeDraft). */
export function shownPipelineCode(
  opening: boolean,
  draft: PipelineCodeDraft | null,
  generated: string,
  keep = false,
): string {
  if (opening) return "";
  return resolveCodeDraft(draft, generated, keep);
}

export function resolveCodeDraft(
  draft: PipelineCodeDraft | null,
  generated: string,
  /** Keep the draft regardless of its base — the view-only studio, where
   *  staging can't change under it and the draft IS the deployed code. */
  keep = false,
): string {
  if (!draft) return generated;
  if (keep) return draft.text;
  return draft.basedOn === generated ? draft.text : generated;
}

/** A stage's encodings: what it writes, and — the source only — what it
 *  reads (TTTL-268). */
interface StageEncodings {
  output: Encoding;
  input?: Encoding;
}

function toStage(
  resource: StagedResource,
  encodings: StageEncodings,
  schemas: StagedSchema[],
  refFor: (schema: StagedSchema) => string,
): StageConfig | null {
  const target = parseResourceId(resource.id);
  if (!target) return null;
  const attached = schemas.find((s) =>
    s.attachments.some((a) => a.id === resource.id),
  );
  // Key order follows StageConfig: input before output encoding.
  return {
    service_name: target.serviceName,
    resource: target.resourceName,
    ...(encodings.input ? { input_encoding: encodings.input } : {}),
    output_encoding: encodings.output,
    ...(attached ? { schema_id: refFor(attached) } : {}),
    ...(resource.routeId ? { route_id: resource.routeId } : {}),
  };
}

interface StagedSections {
  sources: StagedResource[];
  transforms: StagedResource[];
  sinks: StagedResource[];
  schemas: StagedSchema[];
}

// The raw ref a stage keeps for a schema row the block can't carry (a
// service-less legacy id): the id's tail.
const schemaEntryId = (schema: StagedSchema) =>
  parseSchemaRowId(schema.id)?.entryId ?? schema.id.replace(/^schema:/, "");

// The staged schemas some staged stage actually references — the ones the
// schemas block must carry.
function referencedSchemas(staged: StagedSections): StagedSchema[] {
  const stagedStageIds = new Set(
    [...staged.sources, ...staged.transforms, ...staged.sinks].map((r) => r.id),
  );
  return staged.schemas.filter((s) =>
    s.attachments.some((a) => stagedStageIds.has(a.id)),
  );
}

// The schemas-block entries for every schema a staged stage references,
// aliased by the schema's real name (deduped) — stage refs point at these
// aliases. Each entry names the service its row's id carries (schemas live
// per service, TTTL-319) and the registry schema by NAME, the form the
// code view shows (schemaRefs.ts turns it into the registry ID on
// save/deploy, the staged schema's own ID settling any name shared with
// another entry). A row whose id names no service gets no entry.
function buildSchemasBlock(
  staged: StagedSections,
): { entries: SchemaConfig[]; aliasFor: Map<string, string> } | null {
  const taken = new Set<string>();
  const entries: SchemaConfig[] = [];
  const aliasFor = new Map<string, string>();
  for (const schema of referencedSchemas(staged)) {
    const owner = parseSchemaRowId(schema.id);
    if (!owner) continue;
    const alias = schemaAlias(schema.name, taken);
    taken.add(alias);
    aliasFor.set(schema.id, alias);
    entries.push({
      id: alias,
      service_name: owner.serviceName,
      resource: schema.name,
    });
  }
  return entries.length > 0 ? { entries, aliasFor } : null;
}

/** The template, carrying whatever top-level fields the code had beyond
 *  its stages (operation_types, checkpoint, a name of its own …). With none
 *  it is the template text itself, so untouched code still reads as not
 *  edited. */
function templateWith(fields: Record<string, unknown>): string {
  const extra = Object.keys(fields).filter(
    (key) => !(key === "name" && fields.name === TEMPLATE_NAME),
  );
  if (extra.length === 0) return PIPELINE_CODE_TEMPLATE;
  return JSON.stringify(
    {
      ...fields,
      name: typeof fields.name === "string" ? fields.name : TEMPLATE_NAME,
      source: PLACEHOLDER_STAGES.source,
      middlewares: [PLACEHOLDER_STAGES.middleware],
      sinks: [PLACEHOLDER_STAGES.sink],
    },
    null,
    2,
  );
}

function nothingStaged(staged: StagedSections): boolean {
  const lists = [
    staged.sources,
    staged.transforms,
    staged.sinks,
    staged.schemas,
  ];
  return lists.every((list) => list.length === 0);
}

// Provider-encoded stage list; unresolvable resource ids drop out.
function toStages(
  resources: StagedResource[],
  encodingsFor: (resource: StagedResource) => StageEncodings,
  schemas: StagedSchema[],
  refFor: (schema: StagedSchema) => string,
): StageConfig[] {
  return resources
    .map((r) => toStage(r, encodingsFor(r), schemas, refFor))
    .filter((s): s is StageConfig => s !== null);
}

// A change capture source's slot setting goes back onto the source stage its
// staged row produced.
function keepSlot(
  stage: StageConfig | undefined,
  row: StagedResource | undefined,
  slots: Record<string, SlotIntent> | undefined,
) {
  const slot = row && slots?.[row.id];
  if (stage && slot) stage.slot = slot;
}

/** What a pipeline doc carries beyond its stageable structure — kept
 *  across a parse (pipelineStagesFromCode) → generate round trip so
 *  switching views is lossless: `fields` are the top-level keys other than
 *  source/middlewares/sinks/schemas (name, checkpoint, …) in document
 *  order; `encodings` the per-stage output_encoding by staged row id. */
export interface PipelineExtras {
  fields: Record<string, unknown>;
  encodings: Record<string, Encoding>;
  /** The source's replication slot setting, by staged row id, kept as the
   *  document had it: a change capture source's takeover or adoption. */
  slots?: Record<string, SlotIntent>;
  /** The source's `input_encoding`, by staged row id (TTTL-268): kept from
   *  the document or chosen in Stages. Keyed by row so a replaced source
   *  starts without one. */
  inputEncodings?: Record<string, Encoding>;
}

export const NO_EXTRAS: PipelineExtras = { fields: {}, encodings: {} };

export function buildPipelineCode(
  staged: StagedSections,
  extras: PipelineExtras = NO_EXTRAS,
): string {
  // Fresh pipeline — nothing staged yet: the template, as real text, with
  // every reference value left "" (pipelineTemplate.ts). Being the
  // generated document, it counts as "not edited" until the user types,
  // so Full → Stages stays a plain switch; the empty values wear ghost
  // hints (emptySlotHints.ts); as placeholders they carry no lint mark.
  if (nothingStaged(staged)) return templateWith(extras.fields);

  const { sources, transforms, sinks, schemas } = staged;
  const block = buildSchemasBlock(staged);
  const refFor = (schema: StagedSchema) =>
    block?.aliasFor.get(schema.id) ?? schemaEntryId(schema);
  // The passthrough fields keep their document order; a name they lack
  // gets the template's. The structural sections follow.
  const doc: Record<string, unknown> = {
    ...extras.fields,
    name:
      typeof extras.fields.name === "string"
        ? extras.fields.name
        : "my-pipeline",
  };
  const encodingOf = (
    r: StagedResource,
    fallback: (r: StagedResource) => Encoding,
  ) => extras.encodings[r.id] ?? fallback(r);

  const [source] = toStages(
    sources.slice(0, 1),
    (r) => ({
      output: encodingOf(r, (r) => defaultEncoding(r.provider)),
      input: extras.inputEncodings?.[r.id] ?? defaultInputEncoding(r.provider),
    }),
    schemas,
    refFor,
  );
  keepSlot(source, sources[0], extras.slots);
  // The document keeps the template's shape: a missing source or sink is
  // its placeholder entry (lints as a warning — the pipeline can't run
  // without it), and middlewares, which are optional, are an empty list
  // rather than a placeholder so a complete source → sink pipeline stays
  // runnable. Only the all-empty template carries a middleware placeholder.
  doc.source = source ?? PLACEHOLDER_STAGES.source;

  // Middlewares always hand JSON to the next stage, like /studio's
  // buildPrefillPipeline.
  const middlewares = toStages(
    transforms,
    (r) => ({ output: encodingOf(r, () => "json") }),
    schemas,
    refFor,
  );
  doc.middlewares = middlewares;

  const sinkStages = toStages(
    sinks,
    (r) => ({ output: encodingOf(r, (r) => defaultEncoding(r.provider)) }),
    schemas,
    refFor,
  );
  doc.sinks = sinkStages.length > 0 ? sinkStages : [PLACEHOLDER_STAGES.sink];

  if (block) doc.schemas = block.entries;

  return JSON.stringify(doc, null, 2);
}

// The graph strip's unresolved cards — /studio marked cards whose service
// didn't exist; here every lint error (structural + reference) whose path
// points into a stage lands on that stage's graph node, keyed by
// buildPipelineGraph's node ids (source / mw-<i> / sink-<i>). Errors with
// no stage card (root, schemas, malformed sections) are dropped.
export function stageErrorsByNodeId(
  errors: Array<{ path: Array<string | number>; message: string }>,
): Map<string, string[]> {
  const byNode = new Map<string, string[]>();
  for (const error of errors) {
    const [section, index] = error.path;
    const id =
      section === "source"
        ? "source"
        : (section === "middlewares" || section === "sinks") &&
            typeof index === "number"
          ? `${section === "middlewares" ? "mw" : "sink"}-${index}`
          : null;
    if (id === null) continue;
    const messages = byNode.get(id) ?? [];
    messages.push(error.message);
    byNode.set(id, messages);
  }
  return byNode;
}
